import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { readPersisted, writePersisted } from "../hooks/persistentCache";
import { Loader2, Network, Search } from "lucide-react";

const KIND_ACCENT = {
  material: "#00E5FF", footwear: "#F1A817", attire: "#A855F7", headwear: "#EC4899",
  body: "#31A24C", presentation: "#0066FF", environment: "#14B8A6", action: "#E60023",
};

export default function Concepts() {
  const [concepts, setConcepts] = useState(() => readPersisted("concepts"));
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/concepts")
      .then(({ data }) => { setConcepts(data.concepts || []); writePersisted("concepts", data.concepts || []); })
      .catch(() => setConcepts((c) => c || []));
  }, []);

  const grouped = useMemo(() => {
    if (!concepts) return {};
    const list = q
      ? concepts.filter((c) => c.label.toLowerCase().includes(q.toLowerCase()) || c.key.includes(q.toLowerCase()))
      : concepts;
    return list.reduce((acc, c) => {
      (acc[c.kind] = acc[c.kind] || []).push(c);
      return acc;
    }, {});
  }, [concepts, q]);

  if (!concepts) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-[#00E5FF]" size={30} /></div>;

  const kinds = Object.keys(grouped).sort();

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 pt-6 pb-16" data-testid="concepts-page">
      <div className="relative rounded-3xl overflow-hidden mb-8 border border-white/[0.08] bg-gradient-to-br from-[#0066FF]/20 via-[#0F1117] to-[#00E5FF]/10 p-7 sm:p-10">
        <div className="absolute -right-12 -top-12 w-60 h-60 rounded-full bg-[#00E5FF]/10 blur-3xl pointer-events-none" />
        <div className="relative">
          <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#00E5FF] mb-3"><Network size={13} /> Semantic Index</span>
          <h1 className="font-heading text-4xl sm:text-6xl text-white tracking-tight">Concepts</h1>
          <p className="text-[#9CA3AF] text-sm mt-3 max-w-2xl">Every concept the Semantic Index understands.</p>
        </div>
      </div>

      <div className="relative max-w-md mb-10">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
        <input
          data-testid="concepts-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter concepts…"
          className="w-full bg-[#0A0A0A] border border-[#1C1C1F] rounded-xl pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-[#52525B] focus:outline-none focus:border-[#00E5FF]/40"
        />
      </div>

      {kinds.length === 0 && (
        <div className="border border-dashed border-[#1C1C1F] rounded-2xl py-20 text-center text-[#52525B] font-mono text-xs uppercase tracking-widest">No concepts match</div>
      )}

      <div className="space-y-10">
        {kinds.map((kind) => {
          const accent = KIND_ACCENT[kind] || "#0066FF";
          return (
            <section key={kind} data-testid={`concept-kind-${kind}`}>
              <h2 className="text-[11px] font-mono uppercase tracking-widest mb-3" style={{ color: accent }}>{kind}</h2>
              <div className="flex flex-wrap gap-2">
                {grouped[kind].map((c) => (
                  <Link
                    key={c.key}
                    to={`/c/${c.key}`}
                    data-testid={`concept-chip-${c.key}`}
                    className="group flex items-center gap-2 px-3.5 py-2 rounded-full border transition-colors"
                    style={{ background: `${accent}12`, borderColor: `${accent}2A` }}
                  >
                    <span className="text-sm font-medium text-white group-hover:text-white">{c.label}</span>
                    <span className="text-[11px] font-mono px-1.5 py-0.5 rounded-full" style={{ background: `${accent}20`, color: accent }}>{c.count}</span>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
